/**
 * The MSAL browser instance and the one login request the SPA issues.
 *
 * Ported from the V2 portal's `config/authConfig.ts`. The audience is per flavor rather
 * than fixed: NPE uses a tenanted authority so only TESTTRS work accounts can sign in,
 * while PPE and PROD keep V2's `/consumers` authority for personal Microsoft accounts.
 * Every per-flavor value is a `VITE_` variable declared solely in `.env.<mode>`, so one
 * source tree produces the NPE / PPE / PROD packages WebClient.csproj publishes and a
 * flavor's audience is changed in exactly one place. A missing variable yields an empty
 * string rather than a silent default.
 *
 * Only Microsoft sign-in is ported. The government-identity verification V2 layers on top
 * of it — account creation, the verified official e-mail address, the routing check — is
 * deliberately left out of this slice.
 *
 * Initialization is not done here: `main.tsx` awaits `initialize()` and
 * `handleRedirectPromise()` before the app renders, so no component ever observes a
 * half-initialized instance or races the redirect hand-back.
 */
import { type Configuration, PublicClientApplication } from '@azure/msal-browser';

const read = (value: string | undefined): string => (value ?? '').trim();

const configuration: Configuration = {
  auth: {
    clientId: read(import.meta.env.VITE_MSAL_CLIENT_ID),
    // The authority is the constraint: Entra itself refuses to issue a token to an
    // account outside the audience it names, so nothing else ever reaches the app.
    authority: read(import.meta.env.VITE_MSAL_AUTHORITY),
    // Set in `.env.<mode>` only when the reply URL differs from the page origin.
    redirectUri: read(import.meta.env.VITE_MSAL_REDIRECT_URI) || window.location.origin,
  },
  cache: {
    // Session-scoped, matching V2: the portal carries sensitive law-enforcement context
    // and the sign-in should not outlive the browser session.
    cacheLocation: 'sessionStorage',
  },
};

export const msalInstance = new PublicClientApplication(configuration);

/**
 * The `tid` an admitted account must carry. The authority is the real constraint; this is
 * what the client checks against it (see features/auth/context/AuthProvider.tsx), so
 * `.env.<mode>` must name the same audience in both.
 */
export const expectedTenantId = read(import.meta.env.VITE_MSAL_TENANT_ID).toLowerCase();

/**
 * OIDC scopes only. The profile the header renders is read from the ID token claims, so
 * no Graph call — and therefore no `User.Read` consent — is needed for sign-in.
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  prompt: 'select_account' as const,
};

/**
 * The access token the web API accepts. One app registration serves both the SPA and the
 * API, so the SPA asks for a token addressed to itself, naming the app by its `api://`
 * identifier URI (`api://<clientId>/.default`). Entra only issues a first-party app's
 * token-to-self once the registration pre-authorizes its own client ID for a delegated
 * scope; until then the request fails with AADSTS65003.
 */
export const apiTokenRequest = {
  scopes: [read(import.meta.env.VITE_MSAL_API_SCOPE)],
};
